export class ApiError extends Error {
  readonly status: number
  readonly code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  commissionerToken?: string
  teamToken?: string
  adminToken?: string
  /** Lets a caller tear down a superseded request. Arrow-key pool navigation
   *  changes the selection faster than news comes back. An aborted fetch
   *  rejects with an AbortError DOMException, never an ApiError — callers
   *  have to tell "we moved on" from "it failed". */
  signal?: AbortSignal
}

// Token-injecting fetch wrapper for the JSON API. Tokens ride in headers,
// never in URLs or query strings.
export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, commissionerToken, teamToken, adminToken, signal } = options

  // FormData rides as-is: the browser has to set its own multipart
  // Content-Type, boundary included.
  const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData

  const headers: Record<string, string> = {}
  if (body !== undefined && !isMultipart) headers['Content-Type'] = 'application/json'
  if (commissionerToken) headers['X-Commissioner-Token'] = commissionerToken
  if (teamToken) headers['X-Team-Token'] = teamToken
  if (adminToken) headers['X-Admin-Token'] = adminToken

  let payloadBody: BodyInit | undefined
  if (body === undefined) payloadBody = undefined
  else if (isMultipart) payloadBody = body as FormData
  else payloadBody = JSON.stringify(body)

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    body: payloadBody,
    signal,
  })

  let payload: unknown = null
  try {
    payload = await response.json()
  } catch {
    // Non-JSON body (proxy error page etc.) — fall through to status error.
  }

  if (!response.ok) {
    const { error, code } = (payload ?? {}) as { error?: string; code?: string }
    throw new ApiError(response.status, error ?? `Request failed (${response.status})`, code)
  }

  return payload as T
}
